import React, {
  FC,
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { useApolloClient } from "@apollo/client";

import UserService, { UppUser } from "@app/service/user";
import { IntlContext, ServiceContext } from "@app/provider";
import { resetFormStateCache } from "@app/pages/private/scheduleAGeneticInformationSession/context";

export interface UppAuthController {
  profile: UppUser | null;
  signInByAuthenticationProviderInProgress: boolean;
  startSignInByAuthenticationProvider: () => void;
  endSignInByAuthenticationProvider: () => void;
  setProfile: (profile: UppUser) => void;
  setGuestProfileUid: (uid: string) => void;
  logout: () => Promise<boolean>;
  activationEmail: string | null;
  setActivationEmail: (email: string) => void;
  isVerifiedProfileByToken: boolean | undefined;
  setVerifiedProfileByToken: (value: boolean) => void;
  // UPP-5864: server-side DOB-proof handle, presented on downstream sign-up calls.
  intentToken: string | undefined;
  setIntentToken: (value: string | undefined) => void;
  // UPP-5864/5865: DOB-proof handle the server rejected this session; see useSignupIntent.
  rejectedIntentHandle: string | undefined;
  setRejectedIntentHandle: (value: string | undefined) => void;
}

export const Context = createContext<UppAuthController>({
  profile: null,
  signInByAuthenticationProviderInProgress: false,
  startSignInByAuthenticationProvider: () => undefined,
  endSignInByAuthenticationProvider: () => undefined,
  logout: async () => true,
  setProfile: () => undefined,
  setGuestProfileUid: () => undefined,
  activationEmail: null,
  setActivationEmail: () => undefined,
  isVerifiedProfileByToken: undefined,
  setVerifiedProfileByToken: () => undefined,
  intentToken: undefined,
  setIntentToken: () => undefined,
  rejectedIntentHandle: undefined,
  setRejectedIntentHandle: () => undefined,
});

Context.displayName = "UppAuthContext";

const UppAuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { sessionService, storageServiceBuilder } = useContext(ServiceContext);
  const { changeLanguage } = useContext(IntlContext);
  const client = useApolloClient();
  const profileStorage = storageServiceBuilder
    .withProfileKey()
    .build<UppUser>();
  const [profile, setProfile] = useState<UppUser | null>(profileStorage.get());
  const [guestProfileUid, setGuestProfileUid] = useState<string | null>(null);
  const [activationEmail, setActivationEmail] = useState<string | null>(null);
  const [isVerifiedProfileByToken, setVerifiedProfileByToken] = useState<
    boolean | undefined
  >(undefined);
  const [intentToken, setIntentToken] = useState<string | undefined>(undefined);
  const [rejectedIntentHandle, setRejectedIntentHandle] = useState<
    string | undefined
  >(undefined);
  const [
    signInByAuthenticationProviderInProgress,
    setSignInByAuthenticationProviderInProgress,
  ] = useState(false);

  const setUser = (user: UppUser) => {
    profileStorage.set(user);
    setProfile(user);
  };

  useEffect(() => {
    if (profile?.language) {
      changeLanguage(profile.language);
    }

    (profile?.patientUID ?? guestProfileUid)
      ? heap.identify(
          profile?.patientUID ? profile.patientUID : (guestProfileUid as string)
        )
      : heap.resetIdentity();
  }, [profile, guestProfileUid]);

  useEffect(() => {
    const subscription = sessionService.getTokenSubject().subscribe((token) => {
      if (!token) {
        profileStorage.remove();
        resetFormStateCache();
        setProfile(null);
        setVerifiedProfileByToken(undefined);
        setIntentToken(undefined);
        setRejectedIntentHandle(undefined);
        return false;
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const logout = async () => {
    let isSuccess = false;
    try {
      client.writeQuery({
        query: UserService.loadProfile(),
        data: {
          profile: null,
        },
      });
      await sessionService.logout();
      profileStorage.remove();
      client.cache.reset();
      client.clearStore();
      resetFormStateCache();
      setProfile(null);
      setVerifiedProfileByToken(undefined);
      setIntentToken(undefined);
      setRejectedIntentHandle(undefined);
      isSuccess = true;
    } catch (error) {
      console.log(error);
    }
    return isSuccess;
  };

  const startSignInByAuthenticationProvider = () =>
    setSignInByAuthenticationProviderInProgress(true);

  const endSignInByAuthenticationProvider = () =>
    setSignInByAuthenticationProviderInProgress(false);

  const uppAuthController: UppAuthController = useMemo(
    () => ({
      profile,
      signInByAuthenticationProviderInProgress,
      startSignInByAuthenticationProvider,
      endSignInByAuthenticationProvider,
      setProfile: setUser,
      setGuestProfileUid,
      logout,
      activationEmail: activationEmail,
      setActivationEmail: setActivationEmail,
      isVerifiedProfileByToken,
      setVerifiedProfileByToken,
      intentToken,
      setIntentToken,
      rejectedIntentHandle,
      setRejectedIntentHandle,
    }),
    [
      profile,
      signInByAuthenticationProviderInProgress,
      startSignInByAuthenticationProvider,
      endSignInByAuthenticationProvider,
      setUser,
      setGuestProfileUid,
      logout,
      activationEmail,
      setActivationEmail,
      isVerifiedProfileByToken,
      setVerifiedProfileByToken,
      intentToken,
      setIntentToken,
      rejectedIntentHandle,
      setRejectedIntentHandle,
    ]
  );

  return (
    <Context.Provider value={uppAuthController}>{children}</Context.Provider>
  );
};

export default UppAuthProvider;
